import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  CHANNEL_VALUES,
  getMonthIndex,
  MILLIS,
  MIX_VARIANCE_TYPES,
  MONTH_KEYS,
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY,
  SELECTABLE_VARIANCE_TYPES,
  VARIANCE_MEASURES,
  VARIANCE_SIDE_YEAR,
  VARIANCE_SIDE_YEARS,
  type MixVarianceType,
  type MonthKey,
  type ProductCategory,
  type SelectableVarianceType,
  type VarianceMeasure,
  type VarianceSideYear,
  type VarianceType,
} from '@repo/constants'
import { monthStartString } from '@repo/zod-schemas'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const VARIANCE_QUICK_FILTERS = [
  'fmc',
  'cigarettes',
  'vapour',
  'vuse-disposables',
  'nrt',
] as const
export type VarianceQuickFilter = (typeof VARIANCE_QUICK_FILTERS)[number]

/**
 * The comparison is configured in the page header and committed to the URL by the Compare button, so a
 * comparison can be linked to and survives a reload. Both sides carry the same five params (`…A` / `…B`):
 * the CRT period the version belongs to, the version itself, and the slice of its forecast to read
 * (current/next year plus an inclusive month range).
 */
export const varianceAnalysisSearchSchema = z.object({
  crtPeriodA: monthStartString.optional(),
  // Absent once committed for a side on the live baseline, and for a last-year side, which reads no version.
  versionIdA: z.coerce.number().int().positive().optional(),
  yearA: z.enum(VARIANCE_SIDE_YEARS).optional(),
  monthFromA: z.enum(MONTH_KEYS).optional(),
  monthToA: z.enum(MONTH_KEYS).optional(),
  crtPeriodB: monthStartString.optional(),
  versionIdB: z.coerce.number().int().positive().optional(),
  yearB: z.enum(VARIANCE_SIDE_YEARS).optional(),
  monthFromB: z.enum(MONTH_KEYS).optional(),
  monthToB: z.enum(MONTH_KEYS).optional(),
  // Which preset the quick-filter bar shows as selected. Purely a record of the click: the scope params
  // below are what the queries read, and a preset writes them on selection.
  quickFilter: z.enum(VARIANCE_QUICK_FILTERS).optional(),
  // Which of the decomposed variance measures the table's single value column reports.
  varianceType: z.enum(SELECTABLE_VARIANCE_TYPES).optional(),
  /**
   * Which KPI the comparison is read against. Optional so a link written before AR existed still parses,
   * and defaulted by the page rather than here, the way `varianceType` and `quickFilter` are.
   */
  measure: z.enum(VARIANCE_MEASURES).optional(),
  // The currency rate the figures are displayed in, as a kebab-cased rate name. Display-only: the
  // endpoints always report CAD, so this never reaches a query key and changing it refetches nothing.
  currency: z.string().optional(),
  // Scope params. Written by a quick-filter preset (the first four) or by the filter panel (the rest).
  // `productCategory` is single, unlike every other scope filter: it is the breakdown endpoint's path
  // segment, and it decides both the row dimensions and which mix components can be attributed.
  productCategory: z.enum(PRODUCT_CATEGORIES).optional(),
  // The brand *house* code, not the sales text `brand` carries. Written by a quick-filter preset, never
  // by the (absent) filter panel.
  brandHouseCode: z.array(z.string()).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  packContentSize: z.array(z.string()).optional(),
  productTypeLike: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  province: z.array(z.string()).optional(),
  channel: z.array(z.enum(CHANNEL_VALUES)).optional(),
  ...datatableSearchSchema.shape,
})

export type VarianceAnalysisSearch = z.infer<typeof varianceAnalysisSearchSchema>

/**
 * One fully-configured side of the comparison. `versionId` is `undefined` for a current- or next-year side
 * on the live baseline — the header selector's convention — and for a last-year side, which reads that
 * year's actuals live and has no version at all.
 */
export type VarianceAnalysisSide = {
  crtPeriod: string
  versionId: number | undefined
  year: VarianceSideYear
  monthFrom: MonthKey
  monthTo: MonthKey
}

/**
 * Whether a side of this year reads a version at all, baseline or saved: every year but last year does,
 * which reads actuals only and offers nothing to pick.
 */
export const sideNeedsVersion = (year: VarianceSideYear | undefined) =>
  year !== VARIANCE_SIDE_YEAR.LY

export type VarianceAnalysisComparison = {
  a: VarianceAnalysisSide
  b: VarianceAnalysisSide
}

/** Number of months a side covers, inclusive of both ends. `0` when the range runs backwards. */
export function monthRangeLength(monthFrom?: MonthKey, monthTo?: MonthKey) {
  if (!monthFrom || !monthTo) return 0
  return Math.max(0, getMonthIndex(monthTo) - getMonthIndex(monthFrom) + 1)
}

/**
 * The ten params a comparison is made of — the whole of what the comparison card, and everything else that
 * reasons about a comparison, reads of a page's search.
 *
 * Every page hosting a comparison has these and a great deal else besides: the summary page adds the scope
 * and the selected measure, the drill pages add the heat-map cell and the dimensions drilled so far. None of
 * that is any of this code's business, and taking the whole of one page's schema would both claim a reach it
 * does not have and shut out the other page's.
 *
 * Picked off the schema rather than restated, so the value types cannot drift from the params they validate,
 * while the key list stays written out here: it is the contract, and it should read as one.
 */
export type VarianceSidesSearch = Pick<
  VarianceAnalysisSearch,
  | 'crtPeriodA'
  | 'versionIdA'
  | 'yearA'
  | 'monthFromA'
  | 'monthToA'
  | 'crtPeriodB'
  | 'versionIdB'
  | 'yearB'
  | 'monthFromB'
  | 'monthToB'
>

/**
 * Whether the search names both sides at all — the eight params a side cannot do without, present. The
 * version ids are not among them: absent, a side reads the live baseline.
 *
 * Presence only, deliberately kept apart from whether the two sides can be *compared*: a drill page has
 * nothing whatever to do without them and sends its reader back to the configurator, while sides that are
 * both named but do not line up are a comparison the reader can see and fix, and belong on the page.
 */
export function hasComparisonSides(
  search: VarianceSidesSearch,
): search is Required<Omit<VarianceSidesSearch, 'versionIdA' | 'versionIdB'>> &
  Pick<VarianceSidesSearch, 'versionIdA' | 'versionIdB'> {
  const { crtPeriodA, yearA, monthFromA, monthToA } = search
  const { crtPeriodB, yearB, monthFromB, monthToB } = search

  if (!crtPeriodA || !yearA || !monthFromA || !monthToA) return false
  if (!crtPeriodB || !yearB || !monthFromB || !monthToB) return false

  return true
}

/**
 * A comparison is only meaningful when both sides span the same number of months, so the Compare button
 * stays disabled until every param is set and the two ranges match.
 */
export function resolveComparison(search: VarianceSidesSearch): VarianceAnalysisComparison | null {
  if (!hasComparisonSides(search)) return null

  const { crtPeriodA, versionIdA, yearA, monthFromA, monthToA } = search
  const { crtPeriodB, versionIdB, yearB, monthFromB, monthToB } = search

  const lengthA = monthRangeLength(monthFromA, monthToA)
  const lengthB = monthRangeLength(monthFromB, monthToB)
  if (lengthA === 0 || lengthA !== lengthB) return null

  return {
    a: {
      crtPeriod: crtPeriodA,
      versionId: sideNeedsVersion(yearA) ? versionIdA : undefined,
      year: yearA,
      monthFrom: monthFromA,
      monthTo: monthToA,
    },
    b: {
      crtPeriod: crtPeriodB,
      versionId: sideNeedsVersion(yearB) ? versionIdB : undefined,
      year: yearB,
      monthFrom: monthFromB,
      monthTo: monthToB,
    },
  }
}

/** A version id as a query param: absent for a baseline or last-year side, which the endpoints accept without one. */
const versionIdParam = (versionId: number | undefined) =>
  versionId === undefined ? undefined : String(versionId)

/**
 * The `…A` / `…B` query params every endpoint takes, flattened out of a resolved comparison. Serialized
 * here rather than through `stringifyValues`, which types every key as optional because it drops nullish
 * values — all but the version ids are required, and the client's types say so.
 */
function toSideParams(comparison: VarianceAnalysisComparison) {
  return {
    crtPeriodA: comparison.a.crtPeriod,
    versionIdA: versionIdParam(comparison.a.versionId),
    yearA: comparison.a.year,
    monthFromA: comparison.a.monthFrom,
    monthToA: comparison.a.monthTo,
    crtPeriodB: comparison.b.crtPeriod,
    versionIdB: versionIdParam(comparison.b.versionId),
    yearB: comparison.b.year,
    monthFromB: comparison.b.monthFrom,
    monthToB: comparison.b.monthTo,
  }
}

/**
 * The scope both endpoints are given, so the cards and the table always describe the same slice. It is
 * simply the page's own scope search params — a quick-filter preset has already been resolved into them
 * by the time anything here is called, so nothing below knows presets exist.
 */
export type VarianceAnalysisScope = Pick<
  VarianceAnalysisSearch,
  | 'brandHouseCode'
  | 'materialGroupCode'
  | 'packContentSize'
  | 'productTypeLike'
  | 'productType'
  | 'brand'
  | 'variant'
  | 'province'
> & {
  /** Single and always resolved: it is the breakdown endpoint's own path segment. */
  productCategory: ProductCategory
}

// A scope whose category may be omitted
export type VarianceAnalysisScopeAllCategories = Omit<VarianceAnalysisScope, 'productCategory'> & {
  productCategory?: ProductCategory
}

/** An empty selection narrows nothing, so it is dropped rather than sent as `[]`. */
const notEmpty = (values?: string[]) => (values?.length ? values : undefined)

/**
 * The scope as query params. The page's province filter is the backend's `marking`: the workbook's
 * province axis is this codebase's `marking_en`, whose value set is the provinces plus `EXPORT` and `UM`
 * — so the panel can narrow to a province, but not (yet) to those two.
 */
function toScopeParams(scope: VarianceAnalysisScopeAllCategories) {
  return stringifyValues({
    // Single on this side, a list on the endpoint's, which scopes every category filter the same way. An
    // omitted category is dropped, leaving the endpoint to total across every category.
    productCategory: scope.productCategory ? [scope.productCategory] : undefined,
    brandHouseCode: notEmpty(scope.brandHouseCode),
    materialGroupCode: notEmpty(scope.materialGroupCode),
    packContentSize: notEmpty(scope.packContentSize),
    productTypeLike: notEmpty(scope.productTypeLike),
    productType: notEmpty(scope.productType),
    brand: notEmpty(scope.brand),
    variant: notEmpty(scope.variant),
    marking: notEmpty(scope.province),
  })
}

/** The URL path segment the breakdown endpoints take (`/crt/variance-analysis/fmc/…`). */
function toCategoryPath(productCategory: ProductCategory) {
  return productCategory.toLowerCase() as Lowercase<ProductCategory>
}

async function getVarianceTotals(
  comparison: VarianceAnalysisComparison,
  scope: VarianceAnalysisScope,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['variance-analysis'].totals.$get(
    { query: { ...toSideParams(comparison), ...toScopeParams(scope) } },
    { init: { signal } },
  )
  return await res.json()
}

export type VarianceTotals = Awaited<ReturnType<typeof getVarianceTotals>>

/**
 * One card's figure, out of a payload that carries both measures.
 *
 * `volume` is the one reading with no measure: a unit count is the same count whichever dollar line is
 * being explained, so it sits beside the per-measure blocks rather than inside each of them. Every other
 * figure is denominated in the measure and is read out of its block.
 *
 * The figures do not share a unit — `total` and the mix components are dollars, `price` is dollars per
 * unit and `volume` is units — so `VARIANCE_TYPE_UNITS` is what decides how one is rendered, and nothing
 * adds `price + volume + mixTotal`. Within mix the roll-up holds: `mixTotal` is the sum of the other seven.
 */
export function varianceTotalOf(
  totals: VarianceTotals | undefined,
  varianceType: VarianceType,
  measure: VarianceMeasure,
): number | undefined {
  return varianceType === 'volume'
    ? totals?.variance.volume
    : totals?.variance[measure][varianceType]
}

/** Feeds the variance cards: one aggregate row over the whole filtered scope, no dimension keys. */
export function useVarianceTotals(
  comparison: VarianceAnalysisComparison | null,
  scope: VarianceAnalysisScope,
) {
  return useQuery({
    queryKey: ['varianceAnalysisTotals', comparison, scope],
    queryFn: ({ signal }) =>
      // `enabled` guarantees a comparison here; the cast keeps the guard in one place.
      getVarianceTotals(comparison as VarianceAnalysisComparison, scope, signal),
    enabled: comparison !== null,
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
  })
}

/** One side's five params, flattened and unsuffixed — what `/current-totals` takes. */
function toCurrentSideParams(side: VarianceAnalysisSide) {
  return {
    crtPeriod: side.crtPeriod,
    versionId: versionIdParam(side.versionId),
    year: side.year,
    monthFrom: side.monthFrom,
    monthTo: side.monthTo,
  }
}

async function getVarianceCurrentTotals(
  side: VarianceAnalysisSide,
  scope: VarianceAnalysisScopeAllCategories,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['variance-analysis']['current-totals'].$get(
    { query: { ...toCurrentSideParams(side), ...toScopeParams(scope) } },
    { init: { signal } },
  )
  return await res.json()
}

export type VarianceCurrentTotals = Awaited<ReturnType<typeof getVarianceCurrentTotals>>

/**
 * One version's NTO, volume and realised rate over the filtered scope, with no comparison. Held back
 * until a side resolves — the welcome tiles read this once for the current version and again for the
 * past SOP they compare against, so either fetch may be waiting on its own version.
 */
export function useVarianceCurrentTotals(
  side: VarianceAnalysisSide | null,
  scope: VarianceAnalysisScopeAllCategories,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['varianceAnalysisCurrentTotals', side, scope],
    queryFn: ({ signal }) =>
      // `enabled` guarantees a side here; the cast keeps the guard in one place.
      getVarianceCurrentTotals(side as VarianceAnalysisSide, scope, signal),
    enabled: side !== null && (options?.enabled ?? true),
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
  })
}

/* -------------------------------------------------------------------------- */
/* By product category                                                         */
/* -------------------------------------------------------------------------- */
async function getVarianceByProductCategory(
  params: { comparison: VarianceAnalysisComparison; scope: VarianceAnalysisScopeAllCategories },
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['variance-analysis']['by-product-category'].$get(
    { query: { ...toSideParams(params.comparison), ...toScopeParams(params.scope) } },
    { init: { signal } },
  )
  return await res.json()
}

export type VarianceByProductCategory = Awaited<ReturnType<typeof getVarianceByProductCategory>>

/** The two-sided figure each measure carries: both sides' value and the move between them. */
export type Variance = VarianceByProductCategory['results'][number]['nto']

/**
 * The rate measure's two-sided figure, widened so a volumeless side reads as no rate: `base`, `compare`
 * and `difference` are all nullable, unlike NTO and volume, which are additive and always have a value.
 */
export type RateVariance = VarianceByProductCategory['results'][number]['rate']

/**
 * Every category's NTO, volume and realised rate over the filtered scope in one read, plus the "All"
 * roll-up as `total`. Held back until a comparison is configured — with no second side there is no move
 * to report, and the tiles show only the headline current figure.
 */
export function useVarianceByProductCategory(
  comparison: VarianceAnalysisComparison | null,
  scope: VarianceAnalysisScopeAllCategories,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['varianceAnalysisByProductCategory', comparison, scope],
    queryFn: ({ signal }) =>
      // `enabled` guarantees a comparison here; the cast keeps the guard in one place.
      getVarianceByProductCategory(
        { comparison: comparison as VarianceAnalysisComparison, scope },
        signal,
      ),
    enabled: comparison !== null && (options?.enabled ?? true),
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
  })
}

/* -------------------------------------------------------------------------- */
/* Stakeholder breakdown by grouping (welcome tiles → detail table)            */
/* -------------------------------------------------------------------------- */

/** Which grouping the stakeholder breakdown table splits the selected measure by. */
export type StakeholderBreakdownDimension =
  | 'brand'
  | 'productType'
  | 'materialGroup'
  | 'priceSegment'
  | 'marking'
  | 'packContentSize'

/** The internal dimension key mapped to the endpoint's URL slug. */
const DIMENSION_SLUG = {
  brand: 'brand',
  productType: 'product-type',
  materialGroup: 'material-group',
  priceSegment: 'price-segment',
  marking: 'marking',
  packContentSize: 'pack-content-size',
} as const satisfies Record<StakeholderBreakdownDimension, string>

async function getVarianceBreakdownByDimension(
  params: {
    comparison: VarianceAnalysisComparison
    scope: VarianceAnalysisScopeAllCategories
    dimension: StakeholderBreakdownDimension
  },
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['variance-analysis'].breakdown[':dimension'].$get(
    {
      param: { dimension: DIMENSION_SLUG[params.dimension] },
      query: { ...toSideParams(params.comparison), ...toScopeParams(params.scope) },
    },
    { init: { signal } },
  )
  return await res.json()
}

export type VarianceDimensionBreakdown = Awaited<ReturnType<typeof getVarianceBreakdownByDimension>>
export type VarianceDimensionBreakdownRow = VarianceDimensionBreakdown['results'][number]

/**
 * Feeds the stakeholder detail table: the selected measure split by Brand or Product Type over the same
 * comparison and scope the tiles already read. Held back until a comparison is configured — with no
 * second side there is no move to break down — and keeps the table on screen across a dimension switch.
 */
export function useVarianceBreakdownByDimension(
  comparison: VarianceAnalysisComparison | null,
  scope: VarianceAnalysisScopeAllCategories,
  dimension: StakeholderBreakdownDimension,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['varianceAnalysisBreakdownByDimension', comparison, scope, dimension],
    queryFn: ({ signal }) =>
      // `enabled` guarantees a comparison here; the cast keeps the guard in one place.
      getVarianceBreakdownByDimension(
        { comparison: comparison as VarianceAnalysisComparison, scope, dimension },
        signal,
      ),
    enabled: comparison !== null && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
  })
}

async function getCurrentVarianceBreakdownByDimension(
  params: {
    side: VarianceAnalysisSide
    scope: VarianceAnalysisScopeAllCategories
    dimension: StakeholderBreakdownDimension
  },
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['variance-analysis'].breakdown[':dimension'].current.$get(
    {
      param: { dimension: DIMENSION_SLUG[params.dimension] },
      query: { ...toCurrentSideParams(params.side), ...toScopeParams(params.scope) },
    },
    { init: { signal } },
  )
  return await res.json()
}

export type VarianceDimensionCurrentBreakdown = Awaited<
  ReturnType<typeof getCurrentVarianceBreakdownByDimension>
>
export type VarianceDimensionCurrentBreakdownRow =
  VarianceDimensionCurrentBreakdown['results'][number]

/**
 * Feeds the current column of the stakeholder detail table: the selected measure split by Brand or
 * Product Type for a single version, with no comparison. The breakdown is current-anchored — a period
 * can have data and no past SOP to sit beside it — so this reads the current figures on their own, and
 * the comparison blocks are layered on where a SOP resolves. Held back until the current side resolves.
 */
export function useCurrentVarianceBreakdownByDimension(
  side: VarianceAnalysisSide | null,
  scope: VarianceAnalysisScopeAllCategories,
  dimension: StakeholderBreakdownDimension,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['varianceAnalysisCurrentBreakdownByDimension', side, scope, dimension],
    queryFn: ({ signal }) =>
      // `enabled` guarantees a side here; the cast keeps the guard in one place.
      getCurrentVarianceBreakdownByDimension(
        { side: side as VarianceAnalysisSide, scope, dimension },
        signal,
      ),
    enabled: side !== null && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
  })
}

/** The material filters the cash dashboard can forward: the page's own multi-selects, sent as they are. */
export type CashBreakdownFilters = {
  productCategory?: ProductCategory[]
  brand?: string[]
}

async function getCashBreakdownByDimension(
  params: {
    side: VarianceAnalysisSide
    filters: CashBreakdownFilters
    dimension: StakeholderBreakdownDimension
  },
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['variance-analysis'].breakdown[':dimension'].current.$get(
    {
      param: { dimension: DIMENSION_SLUG[params.dimension] },
      query: {
        ...toCurrentSideParams(params.side),
        ...stringifyValues({
          productCategory: params.filters.productCategory?.length
            ? params.filters.productCategory
            : undefined,
          brand: notEmpty(params.filters.brand),
        }),
      },
    },
    { init: { signal } },
  )
  return await res.json()
}

export type CashBreakdown = Awaited<ReturnType<typeof getCashBreakdownByDimension>>

/**
 * Feeds the cash dashboard's breakdown modal: NTO, GTO and AR for one version split by a grouping. The same
 * endpoint as {@link useCurrentVarianceBreakdownByDimension}, but scoped by the cash page's filters, which
 * select several categories and brands at once rather than the welcome card's single category.
 */
export function useCashBreakdownByDimension(
  side: VarianceAnalysisSide | null,
  filters: CashBreakdownFilters,
  dimension: StakeholderBreakdownDimension,
) {
  return useQuery({
    queryKey: ['varianceAnalysisCashBreakdown', side, filters, dimension],
    queryFn: ({ signal }) =>
      // `enabled` guarantees a side here; the cast keeps the guard in one place.
      getCashBreakdownByDimension(
        { side: side as VarianceAnalysisSide, filters, dimension },
        signal,
      ),
    enabled: side !== null,
    // No `keepPreviousData`: the rows are keyed by the dimension they were fetched for, so the previous
    // grouping's rows read through a new one would all fall into the unattributed bucket.
    staleTime: MILLIS.MINUTE * 5,
    gcTime: MILLIS.DAY,
    refetchOnWindowFocus: false,
  })
}

type VarianceBreakdownParams = {
  comparison: VarianceAnalysisComparison
  scope: VarianceAnalysisScope
  varianceType: SelectableVarianceType
  /** Part of the params, and so of the query key: unlike the currency, switching it refetches. */
  measure: VarianceMeasure
}

/** What every breakdown endpoint takes: the two sides and the scope. Neither pages nor sorts. */
function toBreakdownRequest(params: VarianceBreakdownParams) {
  return {
    param: { productCategory: toCategoryPath(params.scope.productCategory) },
    query: { ...toSideParams(params.comparison), ...toScopeParams(params.scope) },
  }
}

/**
 * The same request plus the measure, for the endpoints whose figures are denominated in one.
 *
 * `/by-volumes` deliberately does not send it: a unit count is the same count whichever dollar line is
 * being explained, so the endpoint takes no measure and would have nothing to do with one.
 *
 * It does **not** spare the page a fetch. `measure` is a field of {@link VarianceBreakdownParams} and so
 * of the breakdown's query key, which means switching it refetches the volume lens too and gets the same
 * rows back. Narrowing the key per lens would fix that; it has not been worth a lens-shaped query key.
 */
function toMeasuredBreakdownRequest(params: VarianceBreakdownParams) {
  const request = toBreakdownRequest(params)
  return { ...request, query: { ...request.query, measure: params.measure } }
}

/** Each side's volume in units, and the shift between them, also in units. */
async function getVolumeVarianceBreakdown(params: VarianceBreakdownParams, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['variance-analysis'][':productCategory']['by-volumes'].$get(
    toBreakdownRequest(params),
    { init: { signal } },
  )
  return await res.json()
}

/** Each side's realised dollars per unit, and the shift between them, also in dollars per unit. */
async function getRateVarianceBreakdown(params: VarianceBreakdownParams, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['variance-analysis'][':productCategory']['by-rate'].$get(
    toMeasuredBreakdownRequest(params),
    { init: { signal } },
  )
  return await res.json()
}

/**
 * The URL segment each mix measure is served under. Mirrors the backend's `MIX_DIMENSION_PARAMS`, which is
 * the only place these names are defined.
 */
const MIX_VARIANCE_PATHS = {
  mixProvince: 'province',
  mixSegment: 'segment',
  mixBrand: 'brand',
  mixPackContentSize: 'pack-size',
  mixProductType: 'product-type',
  mixSubChannel: 'sub-channel',
  mixOther: 'other',
} as const satisfies Record<MixVarianceType, string>

/**
 * Each side's volume valued at its rate group's shared base rate, and the shift between them. Dollars
 * throughout.
 *
 * One fetcher per measure, built from the same request: the measures differ only in which pooling of the
 * base rate they value the volume at, which is the path segment.
 */
function mixVarianceBreakdownFetcher(mixVarianceType: MixVarianceType) {
  return async (params: VarianceBreakdownParams, signal?: AbortSignal) => {
    const api = getApi()
    const res = await api.crt['variance-analysis'][':productCategory']['by-mix'][
      ':mixDimension'
    ].$get(
      {
        param: {
          productCategory: toCategoryPath(params.scope.productCategory),
          mixDimension: MIX_VARIANCE_PATHS[mixVarianceType],
        },
        query: {
          ...toSideParams(params.comparison),
          ...toScopeParams(params.scope),
          measure: params.measure,
        },
      },
      { init: { signal } },
    )
    return await res.json()
  }
}

/**
 * The breakdown endpoint behind each variance type. A type absent from here has no endpoint and its card is
 * inert: `mixTotal` is the only one, because it is the sum of seven different rate poolings and no single
 * table can show it.
 *
 * This map is the only place the card vocabulary and the endpoint vocabulary meet. It lives on the client
 * because matching a card to an endpoint is a client concern.
 */
export const VARIANCE_TYPE_BREAKDOWNS = {
  volume: getVolumeVarianceBreakdown,
  price: getRateVarianceBreakdown,
  ...(Object.fromEntries(
    MIX_VARIANCE_TYPES.map((varianceType) => [
      varianceType,
      mixVarianceBreakdownFetcher(varianceType),
    ]),
  ) as Record<MixVarianceType, ReturnType<typeof mixVarianceBreakdownFetcher>>),
} as const satisfies Partial<
  Record<SelectableVarianceType, (params: VarianceBreakdownParams, signal?: AbortSignal) => unknown>
>

export type DrillableVarianceType = keyof typeof VARIANCE_TYPE_BREAKDOWNS

export function isDrillableVarianceType(
  varianceType: VarianceType,
): varianceType is DrillableVarianceType {
  return varianceType in VARIANCE_TYPE_BREAKDOWNS
}

/**
 * Any breakdown's response. They all carry the same keys, but the `/by-rate` side is denominated in dollars
 * per unit and its three figures are nullable — a side with no volume realised no rate, and no rate means no
 * shift between two of them. Taking the union rather than one endpoint's shape is what makes a row readable
 * without knowing which one answered.
 */
export type VarianceBreakdown =
  | Awaited<ReturnType<typeof getVolumeVarianceBreakdown>>
  | Awaited<ReturnType<typeof getRateVarianceBreakdown>>
  | Awaited<ReturnType<ReturnType<typeof mixVarianceBreakdownFetcher>>>
export type VarianceBreakdownRow = VarianceBreakdown['results'][number]

/**
 * Feeds the detail table with the whole dataset in the order the endpoint returns it — one row per
 * grouping key, unpaginated and unsorted. The table pages through it locally.
 *
 * Every endpoint returns the same row shape, so which one answered does not change how a row is read; it
 * changes what `base` and `compare` are denominated in, which is why the caller keeps the variance type.
 */
export function useVarianceBreakdown(params: VarianceBreakdownParams | null) {
  const fetchBreakdown =
    params && isDrillableVarianceType(params.varianceType)
      ? VARIANCE_TYPE_BREAKDOWNS[params.varianceType]
      : null

  return useQuery({
    queryKey: ['varianceAnalysisBreakdown', params],
    // `enabled` guarantees both are set here; the casts keep the guard in one place.
    queryFn: ({ signal }) =>
      (fetchBreakdown as NonNullable<typeof fetchBreakdown>)(
        params as VarianceBreakdownParams,
        signal,
      ),
    enabled: fetchBreakdown !== null,
    // Switching measure or scope keeps the grid that is already on screen rather than dropping to
    // `undefined`. Without it every switch tears the whole matrix down to a skeleton and rebuilds it, and
    // the reader loses their place in a grid whose shape is the entire point.
    placeholderData: keepPreviousData,
  })
}

/* -------------------------------------------------------------------------- */
/* The cost-type drill                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The scope a cost-type breakdown reports: one value per dimension, because it describes a single
 * cell of the heat map rather than the whole grid.
 *
 * Generic in the category so a caller that knows which side it is on gets that side's dimensions
 * alone; left unparameterised it is the discriminated union of both, which is what a call that has
 * yet to branch — the fetcher, the hook, the query key — holds. The split mirrors the two schemas
 * the endpoint validates against, and is the same one the heat map already groups by: FMC by price
 * segment, brand house and pack size, the NC categories by product type and sub-channel, all three
 * by marking. Requiring them in the type is what removes the need to check for them: a scope that
 * exists is one the endpoint will accept.
 *
 * The values stay `string` rather than the constants' literal unions. The endpoint is the authority
 * on each value set, and a hand-built URL naming a marking that does not exist *should* come back a
 * bad request rather than be silently dropped here.
 */
export type CostTypeVarianceScope<Category extends ProductCategory = ProductCategory> =
  Category extends typeof PRODUCT_CATEGORY.FMC
    ? {
        productCategory: Category
        marking: string
        priceSegment: string
        brandHouseCode: string
        packContentSize: string
      }
    : {
        productCategory: Category
        marking: string
        productType: string
        subChannel: string
      }

/**
 * One fetcher, two routes — the endpoint splits them by filter shape so that each side can require
 * its own fields, so the call has to pick. Each branch spreads its whole scope into the query, which
 * is exactly the set that route requires: nothing to serialise conditionally, and a missing field
 * would not compile.
 */
async function getCostTypeVariance(
  comparison: VarianceAnalysisComparison,
  scope: CostTypeVarianceScope,
  measure: VarianceMeasure,
  signal?: AbortSignal,
) {
  const api = getApi()
  const init = { init: { signal } }
  const sides = toSideParams(comparison)

  // Each branch spreads its own scope rather than one hoisted object: hoisting loses the narrowing on
  // `productCategory`, and the union that results matches neither route's required fields.
  const res =
    scope.productCategory === PRODUCT_CATEGORY.FMC
      ? await api.crt['variance-analysis'].fmc['by-cost-type'].$get(
          { query: { ...sides, ...scope, measure } },
          init,
        )
      : await api.crt['variance-analysis'].nc['by-cost-type'].$get(
          { query: { ...sides, ...scope, measure } },
          init,
        )

  return await res.json()
}

export type CostTypeVariance = Awaited<ReturnType<typeof getCostTypeVariance>>
export type CostTypeVarianceRow = CostTypeVariance['results'][number]

/**
 * Feeds the cost-type table: under NTO, what each back-margins investment line was worth on both sides
 * and which kind of source put the money there; under AR, the cash ladder instead.
 *
 * The measure is part of the query key because it changes the rows themselves, not just their
 * denomination — unlike on `/totals`, which answers for both at once.
 *
 * Both arguments are nullable and both must be present: a comparison the header has not finished
 * configuring, or a cell that cannot name every dimension its category needs, has nothing to ask for
 * — so the query is held back rather than sent to be rejected.
 */
export function useCostTypeVariance(
  comparison: VarianceAnalysisComparison | null,
  scope: CostTypeVarianceScope | null,
  measure: VarianceMeasure,
) {
  const isEnabled = comparison !== null && scope !== null

  return useQuery({
    queryKey: ['varianceAnalysisCostType', comparison, scope, measure],
    queryFn: ({ signal }) =>
      // `enabled` guarantees both here; the casts keep the guard in one place.
      getCostTypeVariance(
        comparison as VarianceAnalysisComparison,
        scope as CostTypeVarianceScope,
        measure,
        signal,
      ),
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  })
}

/* -------------------------------------------------------------------------- */
/* Drilling into one cost-type cell                                            */
/* -------------------------------------------------------------------------- */

/**
 * The cost-type cell a breakdown below `/by-cost-type` drills into, on top of the heat-map cell.
 *
 * One type for every such leaf, because they all ask the same question of the same cell and differ only
 * in how they answer it. Which sources a given leaf *can* answer is not expressed here: it is a 422
 * from that endpoint, so a scope that exists is one every route will accept.
 *
 * `kpiKey` and `source` stay `string` for the same reason the scope's other values do: the endpoint is
 * the authority on each value set, and a hand-built URL naming something outside it *should* come back
 * a bad request rather than be silently dropped here.
 */
/**
 * The cost-type cell, plus whichever steps the reader has taken since.
 *
 * The scoping filters are all optional and the endpoints do not all accept the same ones — an agreement
 * reaches `/by-customer`, a customer reaches `/by-agreement` and `/by-budget-allocation` — which
 * `getValidVarianceFilterGroups` decides and `clearInvalidFilters` applies before a link is ever built. One
 * type rather than three because that rule lives there, not here: a scope that arrives carrying a group its
 * endpoint cannot take is a 422 the reader can read, not a shape the client should have to model.
 *
 * `sapErp` is absent by design. The RAP forecast, the manual investments and the key-account estimates each
 * name a `hierarchy_erp` and carry no customer number, so an account number could not narrow the forecast
 * side of a comparison at all.
 */
export type CostTypeCellScope<Category extends ProductCategory = ProductCategory> =
  CostTypeVarianceScope<Category> & {
    kpiKey: string
    source: string
    manualInvestmentId?: string
    masterAgreement?: string
    agreementNumber?: string
    hierarchyErp?: string
    budgetAllocation?: string
  }

/**
 * One fetcher, two routes — the same filter-shape split `/by-cost-type` uses, for the same reason: each
 * route requires its own category's dimensions outright.
 */
async function getBudgetAllocationVariance(
  comparison: VarianceAnalysisComparison,
  scope: CostTypeCellScope,
  signal?: AbortSignal,
) {
  const api = getApi()
  const init = { init: { signal } }
  const sides = toSideParams(comparison)

  const res =
    scope.productCategory === PRODUCT_CATEGORY.FMC
      ? await api.crt['variance-analysis'].fmc['by-budget-allocation'].$get(
          { query: { ...sides, ...scope } },
          init,
        )
      : await api.crt['variance-analysis'].nc['by-budget-allocation'].$get(
          { query: { ...sides, ...scope } },
          init,
        )

  return await res.json()
}

export type BudgetAllocationVariance = Awaited<ReturnType<typeof getBudgetAllocationVariance>>

/**
 * Feeds the budget table: what one cell of the cost-type breakdown was worth on both sides, per budget
 * allocation and — where the source has one — per program.
 *
 * Held back until both a comparison and a full scope exist, like its cost-type sibling. Note the
 * endpoint answers for only two of the three sources: a cell it has no budget axis for comes back a
 * **422**, which surfaces here as the query's `error` rather than as an empty table.
 */
export function useBudgetAllocationVariance(
  comparison: VarianceAnalysisComparison | null,
  scope: CostTypeCellScope | null,
) {
  const isEnabled = comparison !== null && scope !== null

  return useQuery({
    queryKey: ['varianceAnalysisBudgetAllocation', comparison, scope],
    queryFn: ({ signal }) =>
      // `enabled` guarantees both here; the casts keep the guard in one place.
      getBudgetAllocationVariance(
        comparison as VarianceAnalysisComparison,
        scope as CostTypeCellScope,
        signal,
      ),
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  })
}

/* -------------------------------------------------------------------------- */
/* The agreement drill                                                         */
/* -------------------------------------------------------------------------- */

/**
 * One fetcher, two routes — the same filter-shape split its two siblings use, for the same reason: each
 * route requires its own category's dimensions outright.
 */
async function getAgreementVariance(
  comparison: VarianceAnalysisComparison,
  scope: CostTypeCellScope,
  signal?: AbortSignal,
) {
  const api = getApi()
  const init = { init: { signal } }
  const sides = toSideParams(comparison)

  const res =
    scope.productCategory === PRODUCT_CATEGORY.FMC
      ? await api.crt['variance-analysis'].fmc['by-agreement'].$get(
          { query: { ...sides, ...scope } },
          init,
        )
      : await api.crt['variance-analysis'].nc['by-agreement'].$get(
          { query: { ...sides, ...scope } },
          init,
        )

  return await res.json()
}

export type AgreementVariance = Awaited<ReturnType<typeof getAgreementVariance>>

/**
 * Feeds the agreement table: what one cell of the cost-type breakdown was worth on both sides, per
 * master agreement and, beneath it, per agreement.
 *
 * Held back until both a comparison and a full scope exist, like its siblings. Only the imported source
 * answers — a manual program is committed against a budget and a leftover against nothing yet, so both
 * come back a **422**, which surfaces here as the query's `error` rather than as an empty table.
 */
export function useAgreementVariance(
  comparison: VarianceAnalysisComparison | null,
  scope: CostTypeCellScope | null,
) {
  const isEnabled = comparison !== null && scope !== null

  return useQuery({
    queryKey: ['varianceAnalysisAgreement', comparison, scope],
    queryFn: ({ signal }) =>
      // `enabled` guarantees both here; the casts keep the guard in one place.
      getAgreementVariance(
        comparison as VarianceAnalysisComparison,
        scope as CostTypeCellScope,
        signal,
      ),
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  })
}

/* -------------------------------------------------------------------------- */
/* The customer drill                                                          */
/* -------------------------------------------------------------------------- */

/**
 * One fetcher, two routes — the same filter-shape split its three siblings use, for the same reason:
 * each route requires its own category's dimensions outright.
 */
async function getCustomerVariance(
  comparison: VarianceAnalysisComparison,
  scope: CostTypeCellScope,
  signal?: AbortSignal,
) {
  const api = getApi()
  const init = { init: { signal } }
  const sides = toSideParams(comparison)

  const res =
    scope.productCategory === PRODUCT_CATEGORY.FMC
      ? await api.crt['variance-analysis'].fmc['by-customer'].$get(
          { query: { ...sides, ...scope } },
          init,
        )
      : await api.crt['variance-analysis'].nc['by-customer'].$get(
          { query: { ...sides, ...scope } },
          init,
        )

  return await res.json()
}

export type CustomerVariance = Awaited<ReturnType<typeof getCustomerVariance>>

/**
 * Feeds the banner table: what one cell of the cost-type breakdown was worth on both sides, per banner
 * and — where the source is recorded per account — per account beneath it.
 *
 * Held back until both a comparison and a full scope exist, like its siblings. All three sources answer,
 * but two cells do not: the commercial-fund leftover is a budget balance and the sales-deals and GO P4P
 * leftovers do not exist, so those come back a **422**, which surfaces here as the query's `error`
 * rather than as an empty table.
 */
export function useCustomerVariance(
  comparison: VarianceAnalysisComparison | null,
  scope: CostTypeCellScope | null,
) {
  const isEnabled = comparison !== null && scope !== null

  return useQuery({
    queryKey: ['varianceAnalysisCustomer', comparison, scope],
    queryFn: ({ signal }) =>
      // `enabled` guarantees both here; the casts keep the guard in one place.
      getCustomerVariance(
        comparison as VarianceAnalysisComparison,
        scope as CostTypeCellScope,
        signal,
      ),
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  })
}
